/**
 * Double-precision 3D vector for orbital mechanics.
 *
 * JavaScript `number` is IEEE 754 double (same precision as C# `double`),
 * so no special handling is needed for precision.
 */
export class Vector3d {
  constructor(
    public x: number,
    public y: number,
    public z: number,
  ) {}

  // --- Static constants ---

  static readonly Zero = new Vector3d(0, 0, 0);
  static readonly One = new Vector3d(1, 1, 1);
  static readonly UnitX = new Vector3d(1, 0, 0);
  static readonly UnitY = new Vector3d(0, 1, 0);
  static readonly UnitZ = new Vector3d(0, 0, 1);

  // --- Properties ---

  /** Euclidean length of the vector. */
  get magnitude(): number {
    return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);
  }

  /** Squared magnitude (avoids a sqrt when only comparing distances). */
  get sqrMagnitude(): number {
    return this.x * this.x + this.y * this.y + this.z * this.z;
  }

  /** Unit vector in the same direction. Returns Zero if magnitude < 1e-15. */
  get normalized(): Vector3d {
    const mag = this.magnitude;
    if (mag < 1e-15) return new Vector3d(0, 0, 0);
    return new Vector3d(this.x / mag, this.y / mag, this.z / mag);
  }

  // --- Instance methods ---

  /** Component-wise addition. */
  add(b: Vector3d): Vector3d {
    return new Vector3d(this.x + b.x, this.y + b.y, this.z + b.z);
  }

  /** Component-wise subtraction. */
  subtract(b: Vector3d): Vector3d {
    return new Vector3d(this.x - b.x, this.y - b.y, this.z - b.z);
  }

  /** Negate all components. */
  negate(): Vector3d {
    return new Vector3d(-this.x, -this.y, -this.z);
  }

  /** Multiply each component by a scalar. */
  scale(s: number): Vector3d {
    return new Vector3d(this.x * s, this.y * s, this.z * s);
  }

  /** Divide each component by a scalar. */
  divideBy(s: number): Vector3d {
    return new Vector3d(this.x / s, this.y / s, this.z / s);
  }

  // --- Static methods ---

  /** Dot product of two vectors. */
  static dot(a: Vector3d, b: Vector3d): number {
    return a.x * b.x + a.y * b.y + a.z * b.z;
  }

  /** Cross product of two vectors. */
  static cross(a: Vector3d, b: Vector3d): Vector3d {
    return new Vector3d(
      a.y * b.z - a.z * b.y,
      a.z * b.x - a.x * b.z,
      a.x * b.y - a.y * b.x,
    );
  }

  /** Euclidean distance between two points. */
  static distance(a: Vector3d, b: Vector3d): number {
    return a.subtract(b).magnitude;
  }

  /** Angle between two vectors in radians. */
  static angle(a: Vector3d, b: Vector3d): number {
    let d = Vector3d.dot(a.normalized, b.normalized);
    d = Math.max(-1.0, Math.min(1.0, d));
    return Math.acos(d);
  }

  /**
   * Check approximate equality within a tolerance.
   * Useful for floating-point comparisons in tests.
   */
  approxEquals(other: Vector3d, tolerance: number = 1e-10): boolean {
    return (
      Math.abs(this.x - other.x) < tolerance &&
      Math.abs(this.y - other.y) < tolerance &&
      Math.abs(this.z - other.z) < tolerance
    );
  }

  toString(): string {
    return `(${this.x.toPrecision(6)}, ${this.y.toPrecision(6)}, ${this.z.toPrecision(6)})`;
  }
}
